/**
 * URL utilities for building consistent URLs across the app
 * Uses environment variables with sensible defaults for Lumiplot domains
 */

/**
 * Get the app origin (SPA domain - main production site)
 * Default: https://lumiplot.ai
 */
export function getAppOrigin(): string {
  return (import.meta.env.VITE_APP_ORIGIN as string) || 'https://lumiplot.ai';
}

/**
 * Get the frontend origin (marketing/blog/docs/legal site)
 * Default: https://blog.lumiplot.ai
 * In local dev, automatically uses localhost:3000 for docs
 */
export function getFrontendOrigin(): string {
  // If explicitly set, use that
  if (import.meta.env.VITE_FRONTEND_ORIGIN) {
    return import.meta.env.VITE_FRONTEND_ORIGIN as string;
  }
  
  // In local dev, use localhost:3000 for docs (Next.js dev server)
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    if (isLocal) {
      return 'http://localhost:3000';
    }
  }
  
  return 'https://blog.lumiplot.ai';
}

/**
 * Get the blog base path
 * Default: /blog
 */
export function getBlogBasePath(): string {
  return (import.meta.env.VITE_BLOG_BASE_PATH as string) || '/blog';
}

/**
 * Get the help/docs base path
 * Default: /help
 */
export function getHelpBasePath(): string {
  return (import.meta.env.VITE_HELP_BASE_PATH as string) || '/help';
}

/**
 * Get the API base URL
 * Default: https://lumiplot-api.onrender.com
 */
export function getApiBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL as string) || 'https://lumiplot-api.onrender.com';
}

/**
 * Get the WebSocket URL
 * If VITE_WS_URL is set, use it; otherwise derive from VITE_API_BASE_URL
 * Default: wss://lumiplot-api.onrender.com (or ws://localhost:5001 in dev)
 */
export function getWebSocketUrl(): string {
  // Explicit override
  const envUrl = (import.meta.env.VITE_WS_URL as string | undefined);
  if (envUrl && typeof envUrl === 'string' && envUrl.trim().length > 0) {
    return envUrl;
  }

  // Derive from API base URL
  try {
    const apiBase = getApiBaseUrl();
    const url = new URL(apiBase);
    const wsProto = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProto}//${url.host}`;
  } catch {
    // Fallback for localhost
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      const isLocal = host === 'localhost' || host === '127.0.0.1';
      if (isLocal) {
        const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${wsProto}//${host}:5001`;
      }
    }
    return 'wss://lumiplot-api.onrender.com';
  }
}

/**
 * Build a blog URL from a path
 * @param pathRest - Path to append (e.g., '/post/123' or 'post/123')
 * @returns Full blog URL
 */
export function getBlogUrl(pathRest: string = ''): string {
  const frontendOrigin = getFrontendOrigin();
  const blogPath = getBlogBasePath();
  const trimmed = frontendOrigin.endsWith('/') ? frontendOrigin.slice(0, -1) : frontendOrigin;
  const cleanPath = pathRest.startsWith('/') ? pathRest : `/${pathRest}`;
  return `${trimmed}${blogPath}${cleanPath}`;
}

/**
 * Build a help/docs URL from a path
 * @param pathRest - Path to append (e.g., '/getting-started' or 'getting-started')
 * @returns Full help URL
 */
export function getHelpUrl(pathRest: string = ''): string {
  const frontendOrigin = getFrontendOrigin();
  const helpPath = getHelpBasePath();
  const trimmed = frontendOrigin.endsWith('/') ? frontendOrigin.slice(0, -1) : frontendOrigin;
  const cleanPath = pathRest.startsWith('/') ? pathRest : `/${pathRest}`;
  return `${trimmed}${helpPath}${cleanPath}`;
}

/**
 * Build a legal page URL
 * @param page - Legal page name: 'privacy', 'data', or 'terms'
 * @returns Full legal page URL on frontend origin (blog.lumiplot.ai)
 */
export function getLegalUrl(page: 'privacy' | 'data' | 'terms'): string {
  const frontendOrigin = getFrontendOrigin();
  const trimmed = frontendOrigin.endsWith('/') ? frontendOrigin.slice(0, -1) : frontendOrigin;
  return `${trimmed}/${page}`;
}

